// JWTContext.jsx
import PropTypes from 'prop-types';
import React, { createContext, useEffect, useReducer, useState } from 'react';
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';

import { LOGIN, LOGOUT } from 'contexts/auth-reducer/actions';
import authReducer from 'contexts/auth-reducer/auth';

import Loader from 'components/Loader';
import axios from 'utils/axios';
import { BASE_URL } from 'config';
import toast from 'react-hot-toast';
import { requestForToken } from '../fcm/firebase';

const chance = new Chance();

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

const verifyAccesstoken = (accesstoken) => {
  if (!accesstoken) return false;
  const decoded = jwtDecode(accesstoken);
  return decoded.exp > Date.now() / 1000;
};

const setSession = (accesstoken) => {
  if (accesstoken) {
    localStorage.setItem('accesstoken', accesstoken);
    axios.defaults.headers.common.Authorization = `Bearer ${accesstoken}`;
  } else {
    localStorage.removeItem('accesstoken');
    delete axios.defaults.headers.common.Authorization;
  }
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [userDetails, setUserDetails] = useState({});
  const [leadStatus, setLeadStatus] = useState(0);
  const [lead, setLead] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCompanyId, setSelectedCompanyId] = useState(0);
  const [jobTabs, setJobTabs] = useState([]);
  const [isJobTabLoading, setIsJobTabLoading] = useState(false);
  const [customerId, setCustomerId] = useState(0);
  const [fcmToken, setFcmToken] = useState('');

  useEffect(() => {
    const initializeFcmToken = async () => {
      const fetchedToken = await requestForToken();
      setFcmToken(fetchedToken);
    };
    initializeFcmToken();
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const accesstoken = localStorage.getItem('accesstoken');
        if (accesstoken && verifyAccesstoken(accesstoken)) {
          setSession(accesstoken);
          const decoded = jwtDecode(accesstoken);
          const user = decoded.response || decoded;

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user
            }
          });
          setUserDetails(user);
        } else {
          dispatch({ type: LOGOUT });
        }
      } catch (err) {
        console.error(err);
        dispatch({ type: LOGOUT });
      }
    };

    init();

    const intervalId = setInterval(
      () => {
        const accesstoken = localStorage.getItem('accesstoken');
        if (accesstoken && !verifyAccesstoken(accesstoken)) {
          toast.error('Session expired. Logging out...');
          setSession(null);
          dispatch({ type: LOGOUT });
        }
      },
      60 * 60 * 1000
    );

    return () => clearInterval(intervalId);
  }, []);

  const redirectByRole = (roleid) => {
    const map = {
      1: '/admin/dashboard',
      2: '/teacher/dashboard',
      3: '/student/dashboard',
      4: '/manager/dashboard',
      5: '/staff/dashboard',
    };
    return map[roleid] ?? '/';
  };

  const login = async (emailOrToken, password, roleid = 1) => {
    try {
      let accesstoken;

      if (password) {
        const response = await axios.post(`${BASE_URL}auth/login`, {
          email: emailOrToken,
          password,
          configid: 1,
          fcmtoken: fcmToken,
          roleid,
        });

        if (!response.data.status) throw new Error(response.data.message);
        accesstoken = response.data.response;
      } else {
        accesstoken = emailOrToken;
      }

      setSession(accesstoken);
      const decoded = jwtDecode(accesstoken);
      const decodedresponse = decoded.response || decoded;
      console.log('decodedresponse', decodedresponse);

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: decodedresponse
        }
      });

      setUserDetails(decodedresponse);

      localStorage.setItem('accesstoken', accesstoken);
      localStorage.setItem('userid', decodedresponse.userid || decodedresponse.id);
      localStorage.setItem('roleid', decodedresponse.roleid ?? 1);
      localStorage.setItem('userimage', decodedresponse.userimage || '');
      localStorage.setItem('name', decodedresponse.username || decodedresponse.authname || '');

      if (decodedresponse.roleid === 2 || decodedresponse.roleid === 5) {
        localStorage.setItem('studentid', decodedresponse.userid || decodedresponse.id);
      }
      if (decodedresponse.tenantid) localStorage.setItem('tenantid', decodedresponse.tenantid);
      if (decodedresponse.sessionid) localStorage.setItem('sessionid', decodedresponse.sessionid);

      toast.success('Login successful!');

      return redirectByRole(decodedresponse.roleid);
    } catch (error) {
      dispatch({ type: LOGOUT });
      const errorMessage = error?.response?.data?.message || error.message || 'Login failed';
      toast.error(errorMessage);
      return null;
    }
  };

  const register = async (email, password, firstName, lastName) => {
    const id = chance.bb_pin();
    const response = await axios.post('/api/account/register', {
      id,
      email,
      password,
      firstName,
      lastName
    });

    let users = response.data;
    if (localStorage.getItem('users')) {
      const localUsers = JSON.parse(localStorage.getItem('users'));
      users = [...localUsers, { id, email, password, name: `${firstName} ${lastName}` }];
    }

    localStorage.setItem('users', JSON.stringify(users));
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async (email) => {
    console.log('Reset password for:', email);
  };

  const updateProfile = () => {};

  if (!state.isInitialized) return <Loader />;

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        updateProfile,
        redirectByRole,
        lead,
        setLead,
        leadStatus,
        setLeadStatus,
        activeTab,
        setActiveTab,
        userDetails,
        selectedCompanyId,
        setSelectedCompanyId,
        customerId,
        setCustomerId,
        jobTabs,
        setJobTabs,
        isJobTabLoading,
        setIsJobTabLoading
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node
};

export default JWTContext;